/** @jsxRuntime classic */
/** @jsx isolateComponent */

import { useState } from 'react';

import { Icon, Colors, Typography, Link } from '@zapier/design-system';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { css } from '@emotion/core';

import { MEDIA_MAX_WIDTH } from '../../../constants';
import { CTAType, SectionName, useAvroTracking } from '../../../observability';

type Props = {
  /**
   * Text to display as a link below the content
   */
  linkText?: string;
  /**
   * For the link below the content text
   */
  href?: string;
  /**
   * Title, shown both when closed and open.
   */
  title: React.ReactNode;

  /**
   * Content, only shown when open.
   */
  children: React.ReactNode;

  /**
   * Enables Drawer to act as a controlled component
   * so that the parent can auto-close one drawer when another opens.
   */
  isOpen?: boolean;

  /**
   * Callback when the accordion is toggled
   */
  onToggle?: ({ isOpen }: { isOpen?: boolean }) => void;
};

const Styles = labelStyles('Drawer', {
  summary: css({
    ...Typography.pageHeader7,
    margin: '20px 5px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    listStyle: 'none',

    '&::-webkit-details-marker': {
      display: 'none',
    },

    // Remove marker from browser / polyfills
    '&::before': {
      content: '""',
    },

    [MEDIA_MAX_WIDTH.small]: {
      ...Typography.pageHeader9,
    },
  }),
  expandCollapseIcon: css({
    height: '40px',
    width: '40px',
    fill: 'none',
    flexShrink: 0,
    marginRight: '24px',
    top: '20px',
  }),
  content: css({
    ...Typography.paragraph1Short,
    margin: '20px 0px 20px 65px',
    '@media screen and (min-width: 768px)': {
      margin: '20px 65px',
    },
  }),
  link: css({
    alignItems: 'center',
    color: Colors.zapier,
    display: 'flex',
    fontWeight: 600,
    height: '100%',
    margin: '20px 0',
    textDecoration: 'none',
    width: 'max-content',

    '& > p': {
      borderBottom: `1.5px solid ${Colors.transparent}`,
    },

    '& > span': {
      display: 'flex',
      opacity: 0,
      visibility: 'hidden',
    },

    '&:hover': {
      '& > p': {
        borderBottom: `1.5px solid ${Colors.zapier}`,
        transition: '250ms',
      },

      '& > span': {
        opacity: 1,
        transition: '250ms',
        visibility: 'visible',
      },
    },
  }),
});

const SquirclePlusIcon = ({ open }: { open?: boolean }) => (
  <span css={Styles.expandCollapseIcon}>
    <svg
      fill="none"
      height="40"
      viewBox="0 0 40 40"
      width="40"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>{open ? 'plus icon' : 'minus icon'}</title>
      <rect
        height="36"
        rx="15"
        stroke={Colors.zapier}
        strokeWidth="3"
        width="36"
        x="2"
        y="2"
      />
      {!open && <path d="M20 9V31.5" stroke={Colors.zapier} strokeWidth="3" />}
      <path d="M32 20L9.5 20" stroke={Colors.zapier} strokeWidth="3" />
    </svg>
  </span>
);

export const Drawer = (props: Props) => {
  const { children, title, onToggle, href, linkText = 'Learn more' } = props;
  const { emitCTAClickEvent } = useAvroTracking();
  const [isOpen, setIsOpen] = useState(false);
  const isControlled = props.isOpen !== undefined;

  const open = isControlled ? props.isOpen : isOpen;

  const handleClick = (e: React.SyntheticEvent<EventTarget, Event>) => {
    e.preventDefault();
    if (onToggle) onToggle({ isOpen: open });
    if (!isControlled) {
      setIsOpen((prevState) => !prevState);
    }
  };

  return (
    <details open={open}>
      <summary css={Styles.summary} onClick={handleClick} tabIndex={0}>
        <SquirclePlusIcon open={open} />
        {title}
      </summary>
      <div css={Styles.content}>
        {children}
        {linkText && href && (
          <Link
            css={Styles.link}
            href={href}
            onClick={() => {
              emitCTAClickEvent({
                href,
                section: SectionName.FAQSection,
                text: linkText,
                type: CTAType.cta_link,
              });
            }}
          >
            <p>{linkText}</p>{' '}
            <span>
              <Icon name="arrowBigRight" size={18} />
            </span>
          </Link>
        )}
      </div>
    </details>
  );
};
